import { useEffect, useState } from "react";
import { GET_STORE_LIST } from "../store-signup-request/page";
import { useMutation, useQuery } from "@apollo/client";
import ResponsiveCDNImage from "../../../components/common/image/responsive-cdn-image";
import { gql } from "../../../__generated__";
import type {
  AdminGetSellerTossRegistrationByStoreIdQuery,
  AdminGetSellerTossRegistrationByStoreIdQueryVariables,
  AdminResetTossSellerRegistrationMutation,
  AdminResetTossSellerRegistrationMutationVariables,
  AdminUpdateSellerContactNumberByStoreIdMutation,
  AdminUpdateSellerContactNumberByStoreIdMutationVariables,
  GetStoreListQuery,
  UpdateStoreImgMutation,
  UpdateStoreImgMutationVariables,
} from "../../../__generated__/graphql";
import LoadingSpinnerInline from "../../../components/common/loading-spinner/loading-spinner-inline";
import { notify, notifyError } from "../../../utils/toast";
import { AdminListDetailLayout } from "../components/admin-list-detail-layout";
import { AdminStoreTableWithMobileCards } from "../components/admin-store-table-with-mobile-cards";
import { useScrollToDetailPanelOnSelect } from "../hooks/use-scroll-to-detail-panel";

const UPDATE_STORE_IMG = gql(`
  mutation UpdateStoreImg($storeId: Int!, $img: Upload!) {
    updateStoreImg(storeId: $storeId, img: $img) {
      ok
    }
  }
`);

const ADMIN_GET_SELLER_TOSS_REGISTRATION = gql(`
  query AdminGetSellerTossRegistrationByStoreId($storeId: Int!) {
    adminGetSellerTossRegistrationByStoreId(storeId: $storeId) {
      sellerId
      storeId
      storeTitle
      contactNumber
      tossPaymentsSellerId
      tossSellerStatus
      bank
      accountHolder
      accountNumber
      tossPhone
    }
  }
`);

const ADMIN_UPDATE_SELLER_CONTACT_NUMBER = gql(`
  mutation AdminUpdateSellerContactNumberByStoreId(
    $storeId: Int!
    $contactNumber: String!
  ) {
    adminUpdateSellerContactNumberByStoreId(
      storeId: $storeId
      contactNumber: $contactNumber
    ) {
      ok
      error
      previousContactNumber
      contactNumber
    }
  }
`);

const ADMIN_RESET_TOSS_SELLER_REGISTRATION = gql(`
  mutation AdminResetTossSellerRegistration($storeId: Int!) {
    adminResetTossSellerRegistration(storeId: $storeId) {
      ok
      error
      previousTossPaymentsSellerId
      previousTossSellerStatus
    }
  }
`);

type StoreRow = NonNullable<
  GetStoreListQuery["getStoreList"]
>["result"][number];

const SELLER_STATUS_LABEL: Record<string, { label: string; cls: string }> = {
  APPROVED: { label: "인증 완료", cls: "bg-green-100 text-green-700" },
  PARTIALLY_APPROVED: { label: "본인인증 완료", cls: "bg-green-100 text-green-700" },
  APPROVAL_REQUIRED: { label: "본인인증 대기", cls: "bg-yellow-100 text-yellow-700" },
  KYC_REQUIRED: { label: "추가 서류 필요", cls: "bg-orange-100 text-orange-700" },
};

function digitsOnly(value: string) {
  return value.replace(/[^0-9]/g, "");
}

function SellerStatusBadge({ status }: { status: string | null | undefined }) {
  if (!status) {
    return (
      <span className="inline-block rounded-full px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-600">
        미등록
      </span>
    );
  }
  const info = SELLER_STATUS_LABEL[status] ?? {
    label: status,
    cls: "bg-gray-100 text-gray-600",
  };
  return (
    <span
      className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${info.cls}`}
    >
      {info.label}
    </span>
  );
}

export default function AdminStoreManagement() {
  const [selectedStore, setSelectedStore] = useState<StoreRow | null>(null);
  const [searchKeyword, setSearchKeyword] = useState("");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [contactNumberInput, setContactNumberInput] = useState("");

  useScrollToDetailPanelOnSelect(selectedStore);

  const { data, loading, error, refetch } =
    useQuery<GetStoreListQuery>(GET_STORE_LIST);

  const {
    data: tossData,
    loading: tossLoading,
    refetch: refetchToss,
  } = useQuery<
    AdminGetSellerTossRegistrationByStoreIdQuery,
    AdminGetSellerTossRegistrationByStoreIdQueryVariables
  >(ADMIN_GET_SELLER_TOSS_REGISTRATION, {
    skip: !selectedStore,
    variables: { storeId: selectedStore?.id ?? 0 },
    fetchPolicy: "network-only",
  });

  const [updateStoreImg, { loading: updating }] = useMutation<
    UpdateStoreImgMutation,
    UpdateStoreImgMutationVariables
  >(UPDATE_STORE_IMG, {
    onCompleted: () => {
      setSelectedStore(null);
      setSelectedFile(null);
      notify("이미지가 변경되었습니다.");
      refetch();
    },
    onError: (e) => {
      notifyError(e.message);
    },
  });

  const [resetTossRegistration, { loading: resettingToss }] = useMutation<
    AdminResetTossSellerRegistrationMutation,
    AdminResetTossSellerRegistrationMutationVariables
  >(ADMIN_RESET_TOSS_SELLER_REGISTRATION, {
    onCompleted: (res) => {
      const result = res.adminResetTossSellerRegistration;
      if (!result?.ok) {
        notifyError(result?.error ?? "토스 재인증 초기화에 실패했습니다.");
        return;
      }
      notify(
        "토스 콘솔 셀러를 삭제하고 연동을 초기화했습니다. 셀러에게 정산센터에서 새 정보로 인증을 다시 진행하도록 안내해 주세요.",
      );
      refetchToss();
    },
    onError: (e) => notifyError(e.message),
  });

  const [updateSellerContactNumber, { loading: updatingContactNumber }] =
    useMutation<
      AdminUpdateSellerContactNumberByStoreIdMutation,
      AdminUpdateSellerContactNumberByStoreIdMutationVariables
    >(ADMIN_UPDATE_SELLER_CONTACT_NUMBER, {
      onCompleted: (res) => {
        const result = res.adminUpdateSellerContactNumberByStoreId;
        if (!result?.ok) {
          notifyError(result?.error ?? "로그인 번호 변경에 실패했습니다.");
          return;
        }
        notify("사장님 로그인 번호가 변경되었습니다.");
        refetchToss();
      },
      onError: (e) => notifyError(e.message),
    });

  const fullList = data?.getStoreList?.ok ? data.getStoreList.result ?? [] : [];
  const keyword = searchKeyword.trim().toLowerCase();
  const list = keyword
    ? fullList.filter(
        (store) =>
          store.title.toLowerCase().includes(keyword) ||
          String(store.id).includes(keyword),
      )
    : fullList;

  const tossRegistration = tossData?.adminGetSellerTossRegistrationByStoreId;
  const isTossAlreadyReset =
    !tossRegistration?.tossPaymentsSellerId &&
    !tossRegistration?.tossSellerStatus;

  useEffect(() => {
    if (tossRegistration?.contactNumber) {
      setContactNumberInput(tossRegistration.contactNumber);
    } else {
      setContactNumberInput("");
    }
  }, [tossRegistration?.contactNumber, selectedStore?.id]);

  const normalizedContactInput = digitsOnly(contactNumberInput);
  const isContactNumberUnchanged =
    normalizedContactInput === (tossRegistration?.contactNumber ?? "");
  const canSaveContactNumber =
    Boolean(normalizedContactInput) &&
    !isContactNumberUnchanged &&
    normalizedContactInput.startsWith("010");

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const handleUpdateImage = () => {
    if (!selectedStore || !selectedFile) return;
    updateStoreImg({
      variables: {
        storeId: selectedStore.id,
        img: selectedFile,
      },
    });
  };

  const handleResetTossRegistration = () => {
    if (!selectedStore) return;
    const confirmed = window.confirm(
      "토스 셀러 연동을 초기화할까요?\n\n토스 콘솔의 기존 셀러도 삭제됩니다.\n사업자 전환·양도 후 사장님이 새 사업자번호·계좌로 다시 인증하면 콘솔에 새 셀러로 등록됩니다.",
    );
    if (!confirmed) return;
    resetTossRegistration({ variables: { storeId: selectedStore.id } });
  };

  const handleUpdateContactNumber = () => {
    if (!selectedStore || !canSaveContactNumber) return;
    const confirmed = window.confirm(
      `사장님 로그인 번호를 ${normalizedContactInput}(으)로 변경할까요?\n\n변경 후 해당 번호로 로그인해야 합니다.`,
    );
    if (!confirmed) return;
    updateSellerContactNumber({
      variables: {
        storeId: selectedStore.id,
        contactNumber: normalizedContactInput,
      },
    });
  };

  const handleSelectStore = (store: StoreRow) => {
    setSelectedStore(store);
    setSelectedFile(null);
  };

  const handleSelectStoreFromList = (store: {
    id: number;
    title: string;
    img?: string | null;
    address?: string | null;
  }) => {
    const full = list.find((x) => x.id === store.id);
    if (full) handleSelectStore(full);
  };

  if (loading) {
    return (
      <div className="w-full p-4">
        <div className="text-gray-500">로딩 중...</div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="w-full p-4">
        <div className="text-red-600">에러: {error.message}</div>
      </div>
    );
  }
  if (!data?.getStoreList?.ok) {
    return (
      <div className="w-full p-4">
        <div className="text-red-600">
          {data?.getStoreList?.error ?? "데이터를 불러올 수 없습니다."}
        </div>
      </div>
    );
  }

  return (
    <AdminListDetailLayout
      title="매장 관리"
      description="매장을 선택하여 이미지 변경, 토스페이먼츠 재인증 초기화 등의 작업을 수행하세요."
      leftRatio={7}
      rightRatio={3}
      leftPanel={{
        title: "매장 목록",
        searchPlaceholder: "매장명 또는 storeId로 검색",
        searchValue: searchKeyword,
        onSearchChange: setSearchKeyword,
        children: (
          <AdminStoreTableWithMobileCards
            stores={list.map((s) => ({
              id: s.id,
              title: s.title,
              img: s.img,
              address: s.address ?? undefined,
            }))}
            selectionMode="single"
            selectedIds={selectedStore ? [selectedStore.id] : []}
            onSelect={handleSelectStoreFromList}
            emptyText={
              keyword ? "검색 결과가 없습니다." : "등록된 매장이 없습니다."
            }
          />
        ),
      }}
      rightPanel={{
        title: "매장 작업",
        children: selectedStore ? (
          <div className="space-y-6">
            <div className="text-sm text-gray-700">
              <span className="font-medium">선택 매장:</span>{" "}
              {selectedStore.title} (ID: {selectedStore.id})
            </div>

            <div className="space-y-3">
              <p className="text-sm font-semibold text-gray-800">
                매장 이미지 변경
              </p>
              <div>
                <p className="text-sm font-medium text-gray-700 mb-2">
                  현재 이미지
                </p>
                {selectedStore.img ? (
                  <ResponsiveCDNImage
                    src={selectedStore.img}
                    alt={selectedStore.title}
                    className="w-32 h-32 object-cover rounded-lg"
                    sizeRatio={0.32}
                  />
                ) : (
                  <div className="w-32 h-32 bg-gray-200 rounded-lg flex items-center justify-center">
                    <span className="text-gray-400">이미지 없음</span>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  새 이미지 선택
                </label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                  className="w-full p-2 border border-gray-300 rounded-md text-sm"
                />
              </div>

              {selectedFile && (
                <div className="p-3 bg-gray-50 rounded-md text-sm text-gray-700">
                  <p className="font-medium mb-1">선택된 파일</p>
                  <p>{selectedFile.name}</p>
                  <p className="text-xs text-gray-500">
                    {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                  </p>
                </div>
              )}

              <div className="flex gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => {
                    setSelectedStore(null);
                    setSelectedFile(null);
                  }}
                  className="flex-1 px-4 py-2 bg-gray-100 text-gray-800 font-medium rounded-md hover:bg-gray-200"
                >
                  선택 해제
                </button>
                <button
                  type="button"
                  onClick={handleUpdateImage}
                  disabled={!selectedFile}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white font-medium rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {updating ? <LoadingSpinnerInline /> : "변경"}
                </button>
              </div>
            </div>

            <hr className="border-gray-200" />

            <div className="space-y-3">
              <p className="text-sm font-semibold text-gray-800">
                토스페이먼츠 재인증
              </p>
              {tossLoading ? (
                <LoadingSpinnerInline />
              ) : !tossRegistration ? (
                <p className="text-sm text-gray-500">
                  이 매장에 연결된 셀러 계정이 없습니다.
                </p>
              ) : (
                <>
                  <div className="space-y-2 rounded-md border border-gray-200 bg-gray-50 p-3">
                    <p className="text-sm font-medium text-gray-800">
                      사장님 로그인 ID
                    </p>
                    <p className="text-xs text-gray-500">
                      앱 로그인에 사용하는 휴대폰 번호입니다. 재인증 전에
                      번호가 바뀌었으면 먼저 변경해 주세요.
                    </p>
                    <label className="block text-xs font-medium text-gray-600">
                      휴대폰 번호
                    </label>
                    <input
                      type="tel"
                      inputMode="numeric"
                      value={contactNumberInput}
                      onChange={(e) =>
                        setContactNumberInput(digitsOnly(e.target.value))
                      }
                      placeholder="01012345678"
                      className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                    />
                    <button
                      type="button"
                      onClick={handleUpdateContactNumber}
                      disabled={!canSaveContactNumber || updatingContactNumber}
                      className="w-full px-4 py-2 bg-slate-700 text-white font-medium rounded-md hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {updatingContactNumber ? (
                        <LoadingSpinnerInline />
                      ) : (
                        "로그인 번호 변경"
                      )}
                    </button>
                  </div>

                  <dl className="space-y-2 text-sm text-gray-700">
                    <div className="flex justify-between gap-2">
                      <dt className="text-gray-500 shrink-0">토스 셀러 ID</dt>
                      <dd className="text-right break-all">
                        {tossRegistration.tossPaymentsSellerId ?? "미등록"}
                      </dd>
                    </div>
                    <div className="flex justify-between gap-2 items-center">
                      <dt className="text-gray-500 shrink-0">인증 상태</dt>
                      <dd>
                        <SellerStatusBadge
                          status={tossRegistration.tossSellerStatus}
                        />
                      </dd>
                    </div>
                    <div className="flex justify-between gap-2">
                      <dt className="text-gray-500 shrink-0">은행</dt>
                      <dd>{tossRegistration.bank ?? "—"}</dd>
                    </div>
                    <div className="flex justify-between gap-2">
                      <dt className="text-gray-500 shrink-0">계좌번호</dt>
                      <dd>{tossRegistration.accountNumber ?? "—"}</dd>
                    </div>
                    <div className="flex justify-between gap-2">
                      <dt className="text-gray-500 shrink-0">예금주</dt>
                      <dd>{tossRegistration.accountHolder ?? "—"}</dd>
                    </div>
                    <div className="flex justify-between gap-2">
                      <dt className="text-gray-500 shrink-0">인증 번호</dt>
                      <dd>{tossRegistration.tossPhone ?? "—"}</dd>
                    </div>
                  </dl>

                  <p className="text-xs text-gray-500">
                    초기화하면 토스 콘솔의 기존 셀러가 삭제됩니다. 사업자
                    전환·양도처럼 사업자번호가 바뀌는 경우, 이후 셀러는
                    정산센터에서 새 정보로 다시 인증해야 합니다.
                    {isTossAlreadyReset
                      ? " 우리 DB는 이미 초기화된 상태입니다. 버튼을 누르면 콘솔에 남은 셀러만 삭제합니다."
                      : ""}
                  </p>

                  <button
                    type="button"
                    onClick={handleResetTossRegistration}
                    disabled={resettingToss}
                    className="w-full px-4 py-2 bg-amber-600 text-white font-medium rounded-md hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {resettingToss ? (
                      <LoadingSpinnerInline />
                    ) : isTossAlreadyReset ? (
                      "토스 콘솔 셀러 삭제 재시도"
                    ) : (
                      "토스 재인증 초기화"
                    )}
                  </button>
                </>
              )}
            </div>
          </div>
        ) : null,
      }}
    />
  );
}
